import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ItemResponse, TotpSetupResponse } from '@proerp/shared';
import { apiRequest } from '@/lib/apiClient';

/**
 * Self-Service-Verwaltung des zweiten Faktors „Authenticator" (TOTP, ADR 0034) —
 * genutzt von `SecuritySettingsDialog`, neben den Passkeys. Der TOTP-Status kommt
 * aus `/auth/me` (`isTotpEnabled`); nach Einrichten/Entfernen wird er neu geladen.
 */
const CURRENT_USER_KEY = ['auth', 'me'] as const;

/** Schritt 1: neues Geheimnis + otpauth-URL (für den QR-Code) erzeugen. */
export function useEnrollTotpOptions() {
  return useMutation({
    mutationFn: () =>
      apiRequest<ItemResponse<TotpSetupResponse>>('/v1/auth/totp/enroll-options', {
        method: 'POST',
      }),
  });
}

/** Schritt 2: Einrichtung mit einem Code aus der App bestätigen → TOTP aktiv. */
export function useConfirmTotp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      apiRequest<void>('/v1/auth/totp/enroll', {
        method: 'POST',
        body: JSON.stringify({ code }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CURRENT_USER_KEY }),
  });
}

/** Authenticator entfernen (Backend verweigert es, wenn es der letzte Faktor wäre). */
export function useRemoveTotp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiRequest<void>('/v1/auth/totp', { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CURRENT_USER_KEY }),
  });
}
